import { AfterContentInit, Component, ElementRef, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, take, timeout } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../../../environments/environment';
import { HabilitationStatesEnum } from '../../../../models/habilitation-type.model';
import { HabilitationType } from '../../../../models/habilitation-type.model';
import { ModalSelectMoreComponent } from '../modal-select-more/modal-select-more.component';

@Component({
  selector: 'app-filtre-gestion',
  templateUrl: './filtre-gestion.component.html',
})
export class FiltreGestionComponent implements OnChanges, AfterContentInit {

  @Input() type: string;
  @Input() data: any[];
  @Input() typeGestion: string;
  @Output() filtreGestionEvent = new EventEmitter<FormGroup>();
  @Output() effacerFiltreEvent = new EventEmitter<boolean>();
  @Output() sendIdNationalChangeEvent = new EventEmitter<string>();
  @ViewChild('nomPorteurHabilitationInput') nomPorteurHabilitationInput: ElementRef;
  @ViewChild('raisonSocialeHabilitationInput') raisonSocialeHabilitationInput: ElementRef;
  @ViewChild('nomPorteurGestionInput') nomPorteurGestionInput: ElementRef;
  @ViewChild('raisonSocialeTiersGestionInput') raisonSocialeTiersGestionInput: ElementRef;

  LIMIT_STRUCTURES_CHECKBOX = environment.GESTION_HABILITATION_LIMIT_STRUCTURES_CHECKBOX;
  LIMIT_USAGES_CHECKBOX = environment.GESTION_HABILITATION_LIMIT_USAGES_CHECKBOX

  translate: TranslateService;

  filtreForm: FormGroup;
  filtreStructures = '';
  structuresFiltre = [];
  etatsFiltre = [];
  usagesFiltre = [];
  rolesFiltre = [];
  offresFiltre = [];
  initialization: boolean;
  private bsModalRef: BsModalRef;

  isAllStructuresSelected = true;
  isAllUsagesSelected = true;

  habilitationType = HabilitationType;

  totalStructuresCount;
  selectedStructuresCount;
  totalUsagesCount;
  selectedUsagesCount;

  isCollapsed = true;

  constructor(
    public injector: Injector,
    private readonly formBuilder: FormBuilder,
    private readonly modalService: BsModalService,
  ) {
    this.initialization = true;
    this.translate = this.injector.get<TranslateService>(TranslateService);
  }

  ngAfterContentInit() {
    if (this.type === 'Gestion') {
      this.filtreForm
        .get('etats')
        .valueChanges
        .pipe(
          debounceTime(500),
          take(1),
        )
        .subscribe((value) => {
          this.filtreTouched();
          this.reinitialiser();
        });
    }

    setTimeout(() => {
      this.nomPorteurGestionInput?.nativeElement?.focus();
      this.nomPorteurHabilitationInput?.nativeElement?.focus();
      this.raisonSocialeHabilitationInput?.nativeElement?.focus();
      this.raisonSocialeTiersGestionInput?.nativeElement?.focus();
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.type) {
      this.buildFilter();
      if (this.typeGestion ?? this.typeGestion === '') {
        this.configTexts();
      }
    }

    this.addCheckboxesToForm();
  }

  private reinitialiser() {
    // désactiver la checkbox du filtre structure s'il n'y a qu'une seule structure
    this.disableCheckboxStructureIfOnlyOneStructure();
    // désactiver la checkbox du filtre etat s'il n'y a qu'un seul état
    this.disableCheckboxEtatIfOnlyOneEtat();
    this.filtrer();
  }

  private disableCheckboxStructureIfOnlyOneStructure() {
    const controlsName = Object.keys(this.structuresForm.controls);
    if (controlsName.length === 1) {
      this.filtreForm.get('selectAllStructuresCheckbox').disable();
      this.structuresForm.get(controlsName[0]).disable();
    }
  }

  private disableCheckboxEtatIfOnlyOneEtat() {
    const controlsName = Object.keys(this.etatsForm.controls);
    if (controlsName.length === 1) {
      this.etatsForm.get(controlsName[0]).setValue(true);
      this.etatsForm.get(controlsName[0]).disable();
    }
  }

  buildFilter() {
    switch (this.type) {
      case 'Habilitation':
        this.filtreForm = this.formBuilder.group({
          nom: [''],
          identifiantNational: [''],
          roles: this.formBuilder.group({}),
          usages: this.formBuilder.group({}),
          offres: this.formBuilder.group({}),
        });
        this.addCheckboxesToForm();
        break;
      case 'Structure':
        this.filtreForm = this.formBuilder.group({
          raisonSociale: [''],
          identifiantNational: [''],
        });
        break;
      case 'Gestion':
        this.filtreForm = this.formBuilder.group({
          nom: [''],
          identifiantNational: [''],
          idNatTiers: [''],
          raisonSocialeTiers: [''],
          selectAllStructuresCheckbox: new FormControl<boolean>(this.isAllStructuresSelected),
          structures: this.formBuilder.group({}),
          etats: this.formBuilder.group({}),
          selectAllUsagesCheckbox: new FormControl<boolean>(this.isAllUsagesSelected),
          usages: this.formBuilder.group({}),
          offres: this.formBuilder.group({}),
        });
        this.addCheckboxesToForm();
        this.configureAllStructuresSelectedCheckbox();
        if ([HabilitationType.CL.toString(), HabilitationType.CL_PM.toString()].includes(this.typeGestion)) {
          this.configureAllUsagesSelectedCheckbox();
        }
        break;
      default:
        break;
    }
  }

  private configureAllStructuresSelectedCheckbox() {
    this.filtreForm.get('selectAllStructuresCheckbox').valueChanges.subscribe(isChecked => {
      const checkboxes = this.filtreForm.get('structures') as FormArray<FormControl>;
      Object.entries(checkboxes.controls).forEach(control => control[1].patchValue(isChecked, { emitEvent: false }));
    });

    this.filtreForm.get('structures').valueChanges.subscribe(checkboxes => {
      const checkboxesArray = Object.entries(checkboxes);
      const allSelected = checkboxesArray.every(control => control[1]);
      if (this.filtreForm.get('selectAllStructuresCheckbox').value !== allSelected) {
        this.filtreForm.get('selectAllStructuresCheckbox').patchValue(allSelected, { emitEvent: false });
      }
    });
  }

  private configureAllUsagesSelectedCheckbox() {
    this.filtreForm.get('selectAllUsagesCheckbox').valueChanges.subscribe(isChecked => {
      const checkboxes = this.filtreForm.get('usages') as FormArray<FormControl>;
      Object.entries(checkboxes.controls).forEach(control => control[1].patchValue(isChecked, { emitEvent: false }));
    });

    this.filtreForm.get('usages').valueChanges.subscribe(checkboxes => {
      const checkboxesArray = Object.entries(checkboxes);
      const allSelected = checkboxesArray.every(control => control[1]);
      if (this.filtreForm.get('selectAllUsagesCheckbox').value !== allSelected) {
        this.filtreForm.get('selectAllUsagesCheckbox').patchValue(allSelected, { emitEvent: false });
      }
    });
  }

  addCheckboxesToForm() {
    this.usagesFiltre.forEach((u) => {
      this.usagesForm.addControl(u.name?.trim(), new FormControl<boolean>(true));
    });

    this.offresFiltre.forEach((o) => {
      this.offresForm.addControl(o.name, new FormControl<boolean>(true));
    });

    if (this.type === 'Gestion') {
      this.verifyCheckBoxEtats();
      this.structuresFiltre.forEach((s) => {
        if (s.name) {
          this.structuresForm.addControl(s.name, new FormControl<boolean>(true));
        }
      });
      const valueCheckBoxEtats = this.initialization ? true : false;
      /*Lors de la mise à jour des valeurs pour définir le filtre d'habilitations sur ouvert pour la première fois, nous passons par la valeur du patch*/
      this.etatsFiltre.forEach((e) => {
        /*Quand faut-il vérifier que le contrôle pour Ouvert est créé avant de mettre à jour la valeur pour pouvoir tout initialiser sur ouvert*/
        if (e.name === HabilitationStatesEnum.OUVERT) {
          this.etatsForm.addControl(e.name, new FormControl<boolean>(valueCheckBoxEtats));
          this.initialization = false;
        } else {
          /* ne pas faire apparaitre l'etat ABANDONNE sur le filtre */
          if (e.name !== HabilitationStatesEnum.ABANDONNE) {
            this.etatsForm.addControl(e.name, new FormControl<boolean>(false));
          }
        }
      });

      this.updateCounters();
    }

    if (this.type === 'Habilitation') {
      this.rolesFiltre?.forEach((r) => {
        this.rolesForm.addControl(r.name, new FormControl<boolean>(false));
      });
    }
  }

  /**
   * Verifier que les libelles de la partie
   * Etats du filtre sont corrects et mis à jour
   */
  public verifyCheckBoxEtats() {
    if (this.filtreForm.get('etats').value) {
      const ancienLibelles: string[] = [];
      const libellesActualises: string[] = [];
      this.etatsFiltre.forEach((etat) => libellesActualises.push(etat?.name));
      const extractionAncienLibelles = Object.keys(this.etatsForm.controls);
      extractionAncienLibelles.forEach((item) => ancienLibelles.push(item));
      const diff = ancienLibelles.filter((p) => !libellesActualises.includes(p));
      diff?.forEach((d) => this.etatsForm.removeControl(d));
    }
  }

  /**
   * Setters de données du parent
   */
  setHabilitationDataFilter(
    usages: { name: string }[],
    roles: { name: string }[],
    offres: { name: string }[]
  ) {
    this.usagesFiltre = usages;
    this.rolesFiltre = roles;
    this.offresFiltre = offres;
    this.addCheckboxesToForm();
  }

  setGestionDataFilter(
    structures: { name: string }[],
    etats: { name: string }[],
    usages: { name: string }[],
    offres: { name: string }[]
  ) {
    this.structuresFiltre = structures;
    this.etatsFiltre = etats;
    this.usagesFiltre = usages;
    this.offresFiltre = offres;
    this.addCheckboxesToForm();
  }

  /**
   * Cases à cocher dynamiques
   */
  get structuresForm(): FormGroup {
    return this.filtreForm.get('structures') as FormGroup;
  }

  get etatsForm(): FormGroup {
    return this.filtreForm.get('etats') as FormGroup;
  }

  get rolesForm(): FormGroup {
    return this.filtreForm.get('roles') as FormGroup;
  }

  get usagesForm(): FormGroup {
    return this.filtreForm.get('usages') as FormGroup;
  }

  get offresForm(): FormGroup {
    return this.filtreForm.get('offres') as FormGroup;
  }

  /*
   * Fin des cases à cocher dynamiques
   */

  effacerFiltre(): void {
    this.initialization = true;
    this.buildFilter();
    this.effacerFiltreEvent.emit(true);
    this.reinitialiser();
    this.filtrer();
  }

  filtrer(): void {
    if (this.filtreTouched()) {
      this.sendIdNationalChangeEvent.emit(this.filtreForm.value.identifiantNational);
      this.filtreGestionEvent.emit(this.filtreForm); 
    } else {
      this.effacerFiltre();
    }
  }

  // Evalue si le filtre a été modifié
  filtreTouched(): boolean {
    let touched = false;
    switch (this.type) {
      case 'Habilitation':
        if (
          this.filtreForm.value.nom !== '' ||
          this.filtreForm.value.identifiantNational !== '' ||
          Object.values(this.rolesForm.value).some((r: boolean) => r) ||
          Object.values(this.usagesForm.value).some((u: boolean) => u) ||
          Object.values(this.offresForm.value).some((o: boolean) => o)
        ) {
          touched = true;
        }
        break;
      case 'Structure':
        if (
          this.filtreForm.value.raisonSociale !== '' ||
          this.filtreForm.value.identifiantNational !== ''
        ) {
          touched = true;
        }
        break;
      case 'Gestion':
        if (
          this.filtreForm.value.nom !== '' ||
          this.filtreForm.value.identifiantNational !== '' ||
          this.filtreForm.value.idNatTiers !== '' ||
          this.filtreForm.value.raisonSocialeTiers !== '' ||
          Object.values(this.structuresForm.value).some((s: boolean) => s) ||
          Object.values(this.etatsForm.value).some((e: boolean) => e) ||
          Object.values(this.usagesForm.value).some((u: boolean) => u) ||
          Object.values(this.offresForm.value).some((o: boolean) => o)
        ) {
          touched = true;
        }
        break;
      default:
        break;
    }
    return touched;
  }

  configTexts() {
    if (this.typeGestion) {
      const typeGestionTraduction = `habilitation.gestion.${this.typeGestion}.filtre`;
      this.translate.stream(typeGestionTraduction).subscribe((t) => this.filtreStructures = t);
    }
  }

  /*** DRAG SELECTION ***/

  /**
   * Detect all filter bar scroll events
   * @param event
   */
  scroll(event: any) {
    // workaround to reset the focus drag zone after a scroll
    window.scrollTo(window.scrollX, window.scrollY + 10);
    setTimeout(() => window.scrollTo(window.scrollX, window.scrollY - 10), 10);
  }

  /**
   * Handle drag selection
   * @param selectedItems Selected items list
   * @param target List to target
   */
  public handleDragSelection(selectedItems: Array<any>, target: 'structures' | 'usages'): void {
    // ignore single click trigger event for single elements
    if (selectedItems.length <= 1) {
      return;
    }

    const selectedNames = selectedItems.map(x => x.key);
    const value = !selectedItems
      .map(x => x.value)
      .every(x => x === true);

    const checkboxes = this.filtreForm.get(target) as FormArray<FormControl>;
    Object
      .entries(checkboxes.controls)
      .filter((checkbox, index) => selectedNames.includes(checkbox[0]))
      .forEach(control => control[1].patchValue(value, { emitEvent: true }));
  }

  /*** MODAL SELECT MORE ITEMS ***/

  /**
   * Update counters
   */
  private updateCounters() {
    this.totalStructuresCount = Object.values(this.structuresForm.value).length;
    this.selectedStructuresCount = Object.values(this.structuresForm.value).filter(x => x === true).length;
    this.totalUsagesCount = Object.values(this.usagesForm.value).length;
    this.selectedUsagesCount = Object.values(this.usagesForm.value).filter(x => x === true).length;
  }

  /**
   * Open select more items modal
   * @param type Filter type
   */
  public openSelectMoreItemsModal(type: 'structures' | 'usages') {
    const initialState = {
      type: type,
      title: (type === 'structures') ? 'filtre.modal.title_structures' : 'filtre.modal.title_usages',
      subtitle: (type === 'structures') ? 'filtre.modal.subtitle_structures' : 'filtre.modal.subtitle_usages',
      itemsForm: (type === 'structures') ? this.filtreForm.get('structures') : this.filtreForm.get('usages'),
      allCheckboxForm: (type === 'structures') ? this.filtreForm.get('selectAllStructuresCheckbox') : this.filtreForm.get('selectAllUsagesCheckbox'),
    };

    this.bsModalRef = this.modalService.show(ModalSelectMoreComponent, { initialState, backdrop: 'static', keyboard: false });
    this.bsModalRef.content.onClose.subscribe({
      next: res => {
        if (res === 'filter') {
          this.filtreGestionEvent.emit(this.filtreForm);
          this.sendIdNationalChangeEvent.emit(this.filtreForm.value.identifiantNational);
        } else {
          this.effacerFiltre();
        }
        this.updateCounters();
      }
    });
  }

}
